import api from "@/assets/js/utils/api.js";
import {
  showSuccess,
  showError,
  showLoading,
  hideLoading,
  showConfirm,
} from "@/assets/js/utils/alertService";

export default {
  name: "UserDashboard",
  data() {
    return {
      activeTab: "profile",
      isEditing: false,
      selectedOrderStatus: "全部",
      selectedCouponStatus: "available",
      selectedFile: null,
      previewImage: null,
      isDragOver: false,
      currentOrderPage: 1, // 訂單目前頁碼
      ordersPerPage: 5, // 每頁顯示 5 筆
      // ⭐ 控制退貨狀態 modal
      showReturnStatusModal: false, // 控制開啟/關閉
      selectedReturnInfo: null, // 被點擊的退貨資料

      // ✅ 第三方帳號狀態
      oauthAccounts: {
        line: {
          connected: false,
          displayName: "",
          userId: "",
        },
        google: {
          connected: false,
          email: "",
          userId: "",
        },
      },

      // 用戶資料
      userProfile: {
        id: 0,
        fullName: "",
        email: "",
        phoneNumber: "",
        address: "",
        jobTitle: "",
        companyName: "",
        companyAddress: "",
        taxId: "",
        auth: "",
        doctorCheck: false,
        picture: "",
        isActive: true,
        createDate: "",
        updatedDate: "",
      },

      authTypes: {
        Note: { label: "僅客戶建檔", color: "#6b7280" },
        Doctor: { label: "動物醫師", color: "#10b981" },
        Supplier: { label: "廠商(經銷商)", color: "#f59e0b" },
        DrugStore: { label: "廠商(藥局)", color: "#8b5cf6" },
        User: { label: "一般會員", color: "#3b82f6" },
      },

      // ✅ 隱私偏好設定
      privacySettings: {
        personalization: true,
        marketingEmail: false,
        orderNotification: true,
      },

      // 編輯用的資料副本
      editProfile: {},

      // 密碼更改表單
      passwordForm: {
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      },

      // 訂單狀態選項
      orderStatuses: [
        { key: "全部", label: "全部" },
        { key: "待處理", label: "待處理" },
        { key: "處理中", label: "處理中" },
        { key: "已出貨", label: "已出貨" },
        { key: "已完成", label: "已完成" },
        { key: "已取消", label: "已取消" },
        { key: "申請退貨", label: "申請退貨" },
      ],

      // 優惠券狀態選項
      couponStatuses: [
        { key: "available", label: "可用" },
        { key: "used", label: "已使用" },
        { key: "expired", label: "已過期" },
      ],

      // 模擬訂單資料
      orders: [],

      // 優惠券資料
      coupons: [],

      // ✅ 新增訂單詳情彈窗狀態
      showOrderDetailModal: false,
      selectedOrder: null,

      // ✅ 新增:控制每個訂單的展開狀態
      expandedOrders: {},

      // ✅ 申請退/換貨相關
      showReturnModal: false,
      selectedOrderForReturn: null,

      returnFormData: {
        type: "return", // 'return' 或 'exchange'
        returnType: "單一商品退貨",
        reason: "",
        otherReason: "",
        remark: "",
        items: [], // 選中要退/換的商品
      },

      selectedReturnItems: {}, // 追蹤選中的商品 { orderDetailId: { quantity: 1, maxQuantity: 5 } }

      returnReasons: [
        { value: "商品破損/瑕疵", label: "商品破損/瑕疵" },
        { value: "收到錯誤商品", label: "收到錯誤商品" },
        { value: "商品與描述不符", label: "商品與描述不符" },
        { value: "商品功能異常", label: "商品功能異常" },
        { value: "其他原因", label: "其他原因" },
      ],
    };
  },

  computed: {
    showCompanyInfo() {
      const businessAuthTypes = ["Doctor", "Supplier", "DrugStore"];
      return businessAuthTypes.includes(this.userProfile.auth);
    },

    totalOrders() {
      return this.orders.length;
    },

    availableCoupons() {
      return this.coupons.filter((coupon) => coupon.status === "available")
        .length;
    },

    filteredOrders() {
      if (this.selectedOrderStatus === "全部") {
        return this.orders;
      }
      return this.orders.filter(
        (order) => order.status === this.selectedOrderStatus,
      );
    },

    filteredCoupons() {
      return this.coupons.filter(
        (coupon) => coupon.status === this.selectedCouponStatus,
      );
    },

    paginatedOrders() {
      const start = (this.currentOrderPage - 1) * this.ordersPerPage; // 計算起始位置
      return this.filteredOrders.slice(start, start + this.ordersPerPage); // 回傳分頁資料
    },

    totalOrderPages() {
      return Math.ceil(this.filteredOrders.length / this.ordersPerPage); // 總頁數
    },
  },

  async mounted() {
    // ✅ 根據路由參數設定初始 tab

    const routeTab = this.$route.meta?.tab;
    if (routeTab) {
      this.activeTab = routeTab;
    }

    // ✅ 載入用戶資料
    await this.loadUserProfile();
    // ✅ 載入其他資料
    await this.loadOAuthStatus();
    await this.loadCoupons();
    await this.loadOrders();
    window.addEventListener("message", this.handleOAuthMessage);
  },

  beforeUnmount() {
    window.removeEventListener("message", this.handleOAuthMessage);
  },

  methods: {
    async loadOrders() {
      try {
        const res = await api.get("/front/order/list"); // <-- 新增的 API

        if (!res.data.success || !Array.isArray(res.data.data)) {
          hideLoading();
          showError("載入訂單失敗");
          return;
        }

        // ⭐ 後端 → 前端資料轉換
        this.orders = res.data.data.map((o, index) => {
          return {
            id: index + 1, // 因後端沒有 id，用 index 補
            orderNumber: o.OrderNo,
            createdAt: o.OrderDate,
            totalAmount: `NT$ ${o.TotalAmount}`,
            status: this.mapOrderStatus(o.OrderStatus), // ← 轉前端標準格式
            paymentMethod: o.PaymentMethod,
            paymentStatus: o.PaymentStatus,
            items: o.Items.map((i, idx) => {
              // 處理商品名稱（包含變體、贈品標記）
              let displayName = i.ProductName || "未知商品";
              if (i.Price == 0) {
                displayName = `${displayName}（贈品）`;
              } else if (i.VariantName) {
                displayName = `${displayName}（${i.VariantName}）`;
              }

              return {
                id: idx,
                orderDetailId: i.OrderDetailId || idx, // 退貨時需要用到
                productId: i.ProductId, // 退貨時需要用到
                name: displayName, // 訂單列表顯示用
                productName: displayName, // 退貨浮窗顯示用
                variantName: i.VariantName, // 規格資訊
                quantity: i.Quantity,
                price: `NT$ ${i.Price}`,
                subtotal: `NT$ ${(i.Price * i.Quantity).toFixed(0)}`, // 小計
                emoji: "📦",
              };
            }),
          };
        });
      } catch (err) {
        console.error("訂單載入錯誤:", err);
        showError("訂單載入失敗，請稍後再試");
      } finally {
        hideLoading();
      }
    },

    //---------------------------------------------
    // ⭐ 顯示要不要「取消訂單」按鈕
    //---------------------------------------------
    canCancel(order) {
      return order.status === "待處理";
    },

    mapOrderStatus(status) {
      const map = {
        待處理: "待處理",
        處理中: "處理中",
        已完成: "已完成",
        已取消: "已取消",
      };

      return map[status] || status; // 預防 undefined
    },
    // ✅ 從後端載入優惠券資料
    async loadCoupons() {
      try {
        const res = await api.get("/front/coupon"); // 呼叫後端 API

        if (res.data.success && Array.isArray(res.data.data)) {
          // 把後端的資料格式轉成前端使用的格式
          this.coupons = res.data.data.map((c) => {
            // ✅ 判斷類型
            let typeLabel = "";
            let discountType = null;

            switch (c.CouponType) {
              case "Product":
                typeLabel = "商品券";
                break;
              case "Fixed":
                typeLabel = "折扣券";
                discountType = "amount";
                break;
              case "Percentage":
                typeLabel = "折扣券";
                discountType = "percentage";
                break;
              default:
                typeLabel = "一般券";
                break;
            }

            return {
              id: c.CouponId,
              title: c.CouponName,
              description: c.Description || "無說明",
              type: typeLabel, // 類型中文化
              discountType: discountType, // 折扣型態（amount 或 percentage）
              discountValue:
                c.CouponType === "Percentage"
                  ? (100 - c.DiscountValue) / 10
                  : c.DiscountValue,
              expiryDate: c.EndDate,
              status:
                c.Status === "可使用"
                  ? "available"
                  : c.Status === "已使用"
                  ? "used"
                  : "expired",
              productName: c.ProductName || null, // ✅ 商品券顯示商品名稱
            };
          });
        } else {
          showError("目前沒有可用的優惠券");
        }
      } catch (error) {
        hideLoading();
        console.error("載入優惠券失敗:", error);
        showError("載入優惠券失敗，請稍後再試");
      }
    },
    setActiveTab(tab) {
      this.activeTab = tab;

      // ✅ 切換分頁後，捲動到頁面最上方（手機/電腦都有效）
      window.scrollTo({
        top: 0,
        behavior: "smooth", // 平滑滾動
      });
    },

    // 🔹 處理 OAuth 回調訊息 (LINE + Google)
    handleOAuthMessage(event) {
      // 處理 LINE 回調
      if (event.data?.source === "line-callback") {
        const { success, message } = event.data;
        hideLoading();
        if (success) {
          this.loadOAuthStatus();
          showSuccess(message || "LINE 綁定成功！");
        } else {
          showError(message || "LINE 綁定失敗，請重新嘗試。");
        }
      }

      // 🔹 處理 Google 回調 (新增部分)
      if (event.data?.source === "google-callback") {
        const { success, message } = event.data;
        hideLoading();
        if (success) {
          this.loadOAuthStatus();
          showSuccess(message || "Google 帳號綁定成功！");
        } else {
          showError(message || "Google 綁定失敗，請重新嘗試。");
        }
      }
    },

    toggleEditMode() {
      if (this.isEditing) {
        this.cancelEdit();
      } else {
        this.isEditing = true;
        this.editProfile = { ...this.userProfile };
      }
    },

    cancelEdit() {
      this.isEditing = false;
      // ✅ 用 userProfile 的資料還原編輯內容，避免資料清空
      this.editProfile = { ...this.userProfile };
    },

    // ✅ 載入用戶資料
    async loadUserProfile() {
      try {
        const res = await api.get("/front/customer/profile");

        if (res.data.success) {
          const data = res.data.data;
          this.userProfile = {
            id: data.Id || 0,
            fullName: data.FullName || "",
            email: data.Email || "",
            phoneNumber: data.PhoneNumber || "",
            address: data.Address || "",
            jobTitle: data.JobTitle || "",
            companyName: data.CompanyName || "",
            companyAddress: data.CompanyAddress || "",
            taxId: data.TaxId || "",
            auth: data.Auth || "",
            doctorCheck: data.DoctorCheck || false,
            picture: data.Picture || "",
            isActive: data.IsActive || true,
            createDate: data.CreateDate || "",
            updatedDate: data.UpdatedDate || "",
          };

          // 複製到編輯用物件
          this.editProfile = { ...this.userProfile };
        } else {
          showError(res.data.message || "載入用戶資料失敗");
        }
      } catch (error) {
        console.error("載入用戶資料失敗:", error);

        if (error.response?.status === 401) {
          showError("登入已失效，請重新登入");
          this.$router.push("/login");
        } else {
          showError("載入用戶資料失敗，請稍後再試");
        }
      } finally {
        hideLoading();
      }
    },

    // 儲存用戶資料
    async saveProfile() {
      try {
        const updateData = {
          fullName: this.editProfile.fullName,
          jobTitle: this.editProfile.jobTitle,
          companyName: this.editProfile.companyName,
          taxId: this.editProfile.taxId,
          address: this.editProfile.address,
          phoneNumber: this.editProfile.phoneNumber,
        };

        const res = await api.post(
          "/front/customer/update-profile",
          updateData,
        ); // ★ 這裡改成你的後端 API

        if (res.data.success) {
          this.userProfile = { ...this.editProfile };
          this.isEditing = false;
          showSuccess("個人資料已更新");
        } else {
          showError(res.data.message || "更新失敗");
        }
      } catch (error) {
        console.error("更新資料失敗:", error);
        showError("更新失敗，請稍後再試");
      }
    },
    // ✅ 載入第三方帳號綁定狀態
    async loadOAuthStatus() {
      try {
        const res = await api.get("/oauth/check-binding"); // 這是你後端的 API
        if (res.data.success && res.data.data) {
          const { lineLinked, googleLinked } = res.data.data;

          // 更新前端綁定狀態
          this.oauthAccounts.line.connected = lineLinked;
          this.oauthAccounts.google.connected = googleLinked;
        }
      } catch (error) {
        console.error("載入第三方帳號狀態失敗:", error);
      }
    },

    // ✅ 綁定 LINE
    async connectLine() {
      try {
        // 🔹 顯示載入中提示（使用共用方法）
        showLoading("正在連接 LINE...");

        // 🔹 呼叫後端 API 取得 LINE 授權登入網址
        const res = await api.get("/oauth/line/auth-url?type=bind"); // 呼叫後端 LINE 授權 URL 生成端點

        if (res.data.success && res.data.authUrl) {
          // 🔹 打開 LINE 授權視窗
          const authWindow = window.open(
            res.data.authUrl, // 後端產出的 LINE 授權連結
            "lineAuth",
            "width=500,height=600,scrollbars=yes,resizable=yes",
          );

          // 🔹 啟動輪詢檢查是否完成授權（視窗關閉）
          const checkAuth = setInterval(async () => {
            try {
              // ✅ 若視窗關閉，就停止輪詢（不顯示任何提示）
              if (authWindow.closed) {
                clearInterval(checkAuth);
                setTimeout(() => hideLoading(), 1500);
              }
            } catch {
              // 忽略跨域錯誤（不同網域視窗會拋例外）
            }
          }, 1000);
        } else {
          // 後端沒有回傳成功的授權網址
          hideLoading();
          showError("無法取得 LINE 授權連結，請稍後再試！");
        }
      } catch (error) {
        // 🔹 錯誤處理（API 請求失敗）
        hideLoading();
        showError("LINE 綁定失敗，請稍後再試！");
        console.error("LINE 綁定錯誤：", error);
      }
    },

    // ✅ 綁定 Google
    async connectGoogle() {
      try {
        // 🔹 顯示載入中提示
        showLoading("正在連接 Google...");

        // 🔹 呼叫後端 API 取得 Google 授權登入網址
        const res = await api.get("/oauth/google/auth-url?type=bind"); // 注意這裡是 bind 類型

        if (res.data.success && res.data.authUrl) {
          // 🔹 打開 Google 授權視窗
          const authWindow = window.open(
            res.data.authUrl, // 後端產出的 Google 授權連結
            "googleAuth",
            "width=500,height=600,scrollbars=yes,resizable=yes",
          );

          // 🔹 啟動輪詢檢查是否完成授權（視窗關閉）
          const checkAuth = setInterval(async () => {
            try {
              // ✅ 若視窗關閉，就停止輪詢（不顯示任何提示）
              if (authWindow.closed) {
                clearInterval(checkAuth);
                setTimeout(() => hideLoading(), 1500);
              }
            } catch {
              // 忽略跨域錯誤（不同網域視窗會拋例外）
            }
          }, 1000);
        } else {
          // 後端沒有回傳成功的授權網址
          hideLoading();
          showError("無法取得 Google 授權連結，請稍後再試！");
        }
      } catch (error) {
        // 🔹 錯誤處理（API 請求失敗）
        hideLoading();
        showError("Google 綁定失敗，請稍後再試！");
        console.error("Google 綁定錯誤：", error);
      }
    },

    // ✅ 更改密碼
    async changePassword() {
      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        showError("新密碼與確認密碼不一致");
        return;
      }

      if (this.passwordForm.newPassword.length < 6) {
        showError("新密碼長度至少需要 6 個字元");
        return;
      }

      try {
        showLoading("正在更改密碼...");

        const res = await api.post("/front/customer/change-password", {
          oldPassword: this.passwordForm.oldPassword,
          newPassword: this.passwordForm.newPassword,
        });

        if (res.data.success) {
          this.passwordForm = {
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          };
          hideLoading(); // ✅ 成功後先關掉 loading
          await showSuccess("密碼已成功更新，請重新登入");
          this.$router.push("/login");
        } else {
          hideLoading(); // ✅ 錯誤前先確保關閉 loading
          await showError(res.data.message || "密碼更改失敗");
        }
      } catch (error) {
        hideLoading();
        await showError("密碼更改失敗，請稍後再試");
      }
    },
    // 其他方法保持不變
    filterOrders(status) {
      this.selectedOrderStatus = status;
    },

    filterCoupons(status) {
      this.selectedCouponStatus = status;
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("zh-TW");
    },

    getOrderStatusLabel(status) {
      const statusObj = this.orderStatuses.find((s) => s.key === status);
      return statusObj ? statusObj.label : status;
    },

    getCouponStatusLabel(status) {
      const statusObj = this.couponStatuses.find((s) => s.key === status);
      return statusObj ? statusObj.label : status;
    },

    useCoupon(coupon) {
      console.log("使用優惠券:", coupon.title);
      // 可以導航到商品頁面
      this.$router.push("/products");
    },

    // ✅ 獲取權限標籤
    getAuthLabel(authType) {
      return this.authTypes[authType]?.label || "未設定";
    },

    // ✅ 獲取權限顏色
    getAuthColor(authType) {
      return this.authTypes[authType]?.color || "#6b7280";
    },

    previewLicense() {
      window.open(this.userProfile.picture, "_blank");
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },

    handleDrop(event) {
      event.preventDefault();
      this.isDragOver = false;

      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    processFile(file) {
      // 檔案大小檢查（5MB）
      if (file.size > 5 * 1024 * 1024) {
        showError("檔案大小不能超過 5MB");
        return;
      }

      // 檔案類型檢查
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        showError("只支援 JPG、PNG 格式");
        return;
      }

      this.selectedFile = file;

      // 生成預覽
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImage = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    removeImage() {
      this.selectedFile = null;
      this.previewImage = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    // ✅ 上傳醫師執照（一般會員申請醫師權限）
    async uploadDoctorLicense() {
      if (!this.selectedFile) {
        showError("請先選擇執照檔案");
        return;
      }

      try {
        showLoading("正在上傳執照...");

        // 先上傳檔案
        const fileUrl = await this.uploadFile(this.selectedFile);

        // 更新用戶資料，設定 DoctorCheck = 1, Picture = fileUrl, DoctorCheckDate = now
        const updateData = {
          Picture: fileUrl,
          DoctorCheck: true,
        };

        const res = await api.put(
          "/front/customer/doctor-application",
          updateData,
        );

        if (res.data.success) {
          // 更新本地用戶資料
          this.userProfile.picture = fileUrl;
          this.userProfile.doctorCheck = true;
          this.userProfile.doctorCheckDate = new Date().toISOString();

          // 清除上傳相關資料
          this.selectedFile = null;
          this.previewImage = null;

          showSuccess("執照上傳成功！醫師權限申請已提交");
        } else {
          showError(res.data.message || "申請提交失敗");
        }
      } catch (error) {
        console.error("上傳失敗:", error);
        showError("上傳失敗，請稍後再試");
      } finally {
        hideLoading();
      }
    },

    // ✅ 通用檔案上傳方法
    async uploadFile(file) {
      const formData = new FormData();
      formData.append("file", file);

      const res = await api.post("/front/customer/upload-file", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.success) {
        return res.data.fileUrl;
      } else {
        throw new Error(res.data.message || "檔案上傳失敗");
      }
    },

    // ✅ 統一上傳執照方法
    async uploadLicense() {
      if (!this.selectedFile) {
        showError("請先選擇檔案");
        return;
      }

      try {
        // 顯示提示文字
        const uploadTexts = {
          User: "正在上傳獸醫師執照...",
          Supplier: "正在上傳營業執照...",
          DrugStore: "正在上傳藥商許可執照...",
          Doctor: "正在上傳獸醫師執照...",
        };
        showLoading(uploadTexts[this.userProfile.auth] || "正在上傳檔案...");

        // ✅ 統一呼叫後端
        const formData = new FormData();
        formData.append("file", this.selectedFile);

        // 傳給同一個 API，不再分 User / Supplier
        const res = await api.post(
          "/front/customer/doctor-application",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );

        if (res.data.success) {
          // 更新本地資料
          await this.loadUserProfile();
          this.userProfile.picture =
            res.data.fileUrl || this.userProfile.picture;
          this.userProfile.updatedDate = new Date().toISOString();

          // 清除上傳資料
          this.selectedFile = null;
          this.previewImage = null;

          showSuccess(res.data.message || "檔案上傳成功！");
        } else {
          showError(res.data.message || "上傳失敗");
        }
      } catch (error) {
        console.error("上傳失敗:", error);
        showError("上傳失敗，請稍後再試");
      } finally {
        hideLoading();
      }
    },

    // ✅ 重置上傳狀態（當切換編輯模式時）
    resetUploadState() {
      this.selectedFile = null;
      this.previewImage = null;
      this.isDragOver = false;
    },

    // ✅ 開啟訂單詳情
    showOrderDetail(order) {
      this.selectedOrder = order;
      this.showOrderDetailModal = true;
      // 防止背景滾動
      document.body.style.overflow = "hidden";
    },

    // ✅ 關閉訂單詳情
    closeOrderDetail() {
      this.showOrderDetailModal = false;
      this.selectedOrder = null;
      // 恢復背景滾動
      document.body.style.overflow = "";
    },

    // ✅ 從詳情頁取消訂單
    async cancelOrderFromDetail() {
      if (!this.selectedOrder) return;

      // 先關閉詳情彈窗
      this.closeOrderDetail();

      // 延遲一點再執行取消流程,避免彈窗衝突
      setTimeout(() => {
        this.cancelOrder(this.selectedOrder);
      }, 300);
    },

    // --------------------------------------------
    // ⭐ 取消訂單（前端串接後端）
    // --------------------------------------------
    async cancelOrder(order) {
      // 🔹 僅可取消「待處理」
      if (order.status !== "待處理") {
        showError("只有待處理的訂單能取消");
        return;
      }

      // 🔹 確認視窗
      const result = await showConfirm(
        `確定要取消訂單 ${order.orderNumber} 嗎？`,
        "此操作無法復原。",
      );

      if (!result.isConfirmed) return;

      try {
        showLoading("取消訂單中...");

        // 🔹 送到後端
        const res = await api.post("/front/order/cancel", {
          orderNo: order.orderNumber,
        });

        if (res.data.success) {
          // 🔹 修改前端顯示
          order.status = "已取消";

          hideLoading();
          await showSuccess("訂單已取消");

          // 🔹 重新載入訂單（保持 UI 正確）
          await this.loadOrders();
        } else {
          hideLoading();
          await showError(res.data.message || "取消失敗");
        }
      } catch (err) {
        hideLoading();
        console.error("取消訂單錯誤:", err);
        await showError("取消訂單失敗，請稍後再試");
      }
    },
    getDisplayItems(order) {
      // 註解：如果這筆訂單有被展開 → 回傳全部商品
      if (this.expandedOrders[order.id]) {
        return order.items;
      }

      // 註解：未展開 → 只顯示前 5 個
      return order.items.slice(0, 3);
    },

    shouldShowMore(order) {
      // 註解：當商品超過 5 個才顯示「顯示更多/收起」
      return order.items.length > 5;
    },

    toggleOrderExpand(order) {
      // 註解：切換展開/收起狀態
      this.expandedOrders[order.id] = !this.expandedOrders[order.id];
    },

    // ✅ 開啟退/換貨浮窗
    openReturnModal(order) {
      this.selectedOrderForReturn = order;
      this.showReturnModal = true;
      // 初始化每個商品的數量資料
      this.selectedReturnItems = {};
      if (order && order.items) {
        order.items.forEach((item) => {
          this.selectedReturnItems[item.orderDetailId] = {
            selected: false,
            quantity: 0,
            maxQuantity: item.quantity,
          };
        });
      }
      document.body.style.overflow = "hidden"; // 禁止背景滾動
    },

    // ✅ 關閉退/換貨浮窗
    closeReturnModal() {
      this.showReturnModal = false;
      this.selectedOrderForReturn = null;
      this.selectedReturnItems = {}; // 重置選中狀態
      // 重置表單
      this.returnFormData = {
        type: "return",
        returnType: "單一商品退貨",
        reason: "",
        otherReason: "",
        remark: "",
        items: [],
      };
      document.body.style.overflow = ""; // 恢復背景滾動
    },

    // ✅ 切換選中商品
    toggleReturnItem(orderDetailId) {
      const item = this.selectedReturnItems[orderDetailId];
      if (item) {
        item.selected = !item.selected;
        // 如果取消選中，重置數量
        if (!item.selected) {
          item.quantity = 0;
        } else {
          // 如果選中，預設數量為1
          item.quantity = Math.min(1, item.maxQuantity);
        }
      }
    },

    // ✅ 更新商品退貨數量
    updateReturnQuantity(orderDetailId, newQuantity) {
      const item = this.selectedReturnItems[orderDetailId];
      if (item) {
        // 限制在 0 ~ maxQuantity 之間
        item.quantity = Math.max(0, Math.min(newQuantity, item.maxQuantity));
        // 如果數量為 0，自動取消選中
        if (item.quantity === 0) {
          item.selected = false;
        } else {
          item.selected = true;
        }
      }
    },

    // ✅ 檢查是否有選中商品
    hasSelectedReturnItems() {
      return Object.values(this.selectedReturnItems).some(
        (v) => v.selected === true && v.quantity > 0,
      );
    },

    // ✅ 提交退/換貨申請
    async submitReturn() {
      // 驗證
      if (!this.returnFormData.type) {
        showError("請選擇退/換貨類型");
        return;
      }

      if (!this.hasSelectedReturnItems()) {
        showError("請選擇至少一個商品");
        return;
      }

      if (!this.returnFormData.reason) {
        showError("請選擇退/換貨原因");
        return;
      }

      try {
        showLoading("正在提交申請...");

        // 構建要退貨的商品清單
        const items = [];
        if (this.selectedOrderForReturn && this.selectedOrderForReturn.items) {
          for (const item of this.selectedOrderForReturn.items) {
            const returnInfo = this.selectedReturnItems[item.orderDetailId];
            if (returnInfo && returnInfo.selected && returnInfo.quantity > 0) {
              items.push({
                orderDetailId: item.orderDetailId,
                productId: item.productId,
                quantity: returnInfo.quantity, // 使用用戶選擇的數量
                reason: this.returnFormData.reason, // 使用全局選擇的原因
              });
            }
          }
        }

        const submitData = {
          orderNo: this.selectedOrderForReturn.orderNumber,
          type: this.returnFormData.type === "return" ? "退貨" : "換貨",
          returnType: "單一商品退貨",
          reason: this.returnFormData.reason,
          remark: this.returnFormData.remark,
          items: items,
        };

        const res = await api.post("/front/order/return", submitData);
        hideLoading(); // ⭐ 成功記得關閉 loading

        if (res.data.success) {
          await showSuccess(res.data.message); // ⭐ 顯示後端訊息
          setTimeout(() => {
            this.closeReturnModal();
          }, 200);
          await this.loadOrders(); // ⭐ 重新載入訂單狀態
        } else {
          await showError(res.data.message); // ⭐ 顯示後端錯誤訊息
        }
      } catch (error) {
        hideLoading();
        console.error("提交退貨申請錯誤:", error);
        await showError("提交失敗，請稍後再試");
      }
    },
    // 打開退貨狀態視窗
    async openReturnStatusModal(order) {
      try {
        showLoading("載入退貨狀態中...");

        // 1️⃣ 呼叫後端 API
        const res = await api.get(
          `/front/order/return-status?orderNo=${order.orderNumber}`,
        );

        hideLoading();

        if (!res.data.success) {
          showError(res.data.message || "查詢退貨狀態失敗");
          return;
        }

        const r = res.data.data;

        // 🔍 詳細日誌：記錄後端返回的退貨狀態資料
        // console.log("=== 🔍 查詢退貨狀態日誌 ===");
        // console.log("📦 訂單號:", order.orderNumber);
        // console.log("📡 API 回應:", JSON.stringify(res.data, null, 2));
        // console.log("📋 退貨狀態詳情:", {
        //   orderNumber: r.OrderNo,
        //   orderReturnNo: r.OrderReturnNo,
        //   applyDate: r.ApplyDate,
        //   type: r.Type,
        //   reason: r.Reason,
        //   remark: r.Remark,
        //   refundAmount: r.RefundAmount,
        //   reviewStatus: r.Status,
        //   adminNote: r.AdminNote,
        // });
        // console.log("========================\n");

        // 2️⃣ 把後端資料塞進 modal
        this.selectedReturnInfo = {
          orderNumber: r.OrderNo,
          orderReturnNo: r.OrderReturnNo, // 退貨申請編號
          applyDate: r.ApplyDate,
          type: r.Type,
          reasonText: r.Reason,
          remark: r.Remark,
          refundAmount: r.RefundAmount, // 退款金額
          reviewStatus: r.Status, // 審核中 / 通過 / 拒絕
          reviewMessage: r.AdminNote, // 管理員回覆
        };

        // 3️⃣ 打開視窗
        this.showReturnStatusModal = true;
      } catch (error) {
        hideLoading();
        console.error("❌ 退貨狀態查詢錯誤:", error);
        console.error("📡 API 回應:", error.response?.data);
        console.error("🔗 請求 URL:", error.config?.url);
        console.error("📨 請求方法:", error.config?.method);
        showError("查詢失敗，請稍後再試");
      }
    },
    // 關閉視窗
    closeReturnStatusModal() {
      this.showReturnStatusModal = false;
      this.selectedReturnInfo = null;
    },

    // ============================
    // 點擊「總訂單」 → 我的訂單分頁
    // ============================
    goToOrders() {
      this.activeTab = "orders"; // 控制顯示訂單分頁
      this.currentOrderPage = 1; // 順便重置分頁到第一頁（體驗比較好）
    },

    // ============================
    // 點擊「可用優惠券」 → 優惠券分頁
    // ============================
    goToCoupons() {
      this.activeTab = "coupons"; // 控制顯示優惠券分頁
      this.selectedCouponStatus = "available"; // 預設顯示可用優惠券
    },
  },
};
